import type { CardColumn } from '@/components/CardTable/type';
import { renderCellContent } from '@/components/CardTable/util';
import type { TRecordType } from '@moneyforward/ap-frontend-components';
import { CSSProperties, memo } from 'react';

export type Props<RecordType extends TRecordType> = {
  className?: string;
  rowKey?: string;
  record?: RecordType;
  index?: number;
  /** Real column definitions (with their real `render`), read once per row. */
  columns: CardColumn<RecordType>[];
};

const getColumnKey = <RecordType extends TRecordType>(
  column: CardColumn<RecordType>,
  fallback: number
): string => {
  if (column.key !== undefined) return String(column.key);
  if (column.dataIndex !== undefined) return String(column.dataIndex);
  return String(fallback);
};

/**
 * One virtualized table row, rendered as a real `<tr>`/`<td>` so it picks up
 * the design system `Table`'s own `rci-table-row`/`rci-table-cell` styling
 * (borders, padding, colors) for free — only the body is custom-rendered;
 * the header keeps using the real `Table`'s normal rendering.
 *
 * `Table` is still fed a render-less copy of the columns (see
 * `TransactionsDetailVirtualTable`) so it has *something* cheap to compute
 * per visible row on every scroll step. The real, expensive cell content
 * (every `TooltipCell`) is built here instead — and this component is
 * `memo`ized, deliberately ignoring the `children` antd hands it, so an
 * already-mounted row isn't re-rendered just because the window shifted
 * while its `record`/`columns`/`index` stayed referentially the same. Only
 * the newly-entering row(s), or a row whose underlying data actually
 * changed (e.g. a refetch swapped in a new `record` for the same key),
 * pay that cost, same as the memoized card layout.
 */
const _VirtualTableRow = <RecordType extends TRecordType>({
  className,
  rowKey,
  record,
  index,
  columns,
}: Props<RecordType>) => {
  if (!record) {
    return <tr className={className} data-row-key={rowKey} />;
  }

  return (
    <tr className={className} data-row-key={rowKey}>
      {columns.map((column, colIndex) => {
        const cellStyle: CSSProperties | undefined =
          column.align === 'right' ? { textAlign: 'right' } : undefined;
        return (
          <td
            key={getColumnKey(column, colIndex)}
            className='rci-table-cell'
            style={cellStyle}
          >
            {renderCellContent(column, record, index ?? 0)}
          </td>
        );
      })}
    </tr>
  );
};
_VirtualTableRow.displayName = '_VirtualTableRow';

// The assertion restores the generic parameter that memo erases (see
// `RecordCard` for the same pattern).
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const VirtualTableRow = memo(
  _VirtualTableRow,
  (prev, next) =>
    prev.rowKey === next.rowKey &&
    prev.record === next.record &&
    prev.columns === next.columns &&
    prev.index === next.index
) as typeof _VirtualTableRow;
VirtualTableRow.displayName = 'VirtualTableRow';
